import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Info, AlertCircle } from 'lucide-react';

interface FormData {
  // Business Information
  businessName: string;
  industry: string;
  yearsInOperation: string;
  annualRevenue: string;
  annualEbitda: string;
  numberOfEmployees: string;

  // Financial Details
  loanDebt: string;
  creditDebt: string;
  inventoryValue: string;
  ffeValue: string;
  ppeValue: string;
  cashValue: string;
  accountsReceivable: string;
  accountsPayable: string;

  // Owner Information
  ownerName: string;
  ownerRole: string;
  exitTimeline: string;
  sellingReason: string;

  // Transaction Goals
  transactionType: string;
  postInvolvement: string;
  priorities: string[];

  // Contact Information
  email: string;
  phone: string;
  preferredContact: string;
  bestTimeToReach: string;
}

interface SellerFormProps {
  onBack: () => void;
}

const SellerForm: React.FC<SellerFormProps> = ({ onBack }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    // Business Information
    businessName: '',
    industry: '',
    yearsInOperation: '',
    annualRevenue: '',
    annualEbitda: '',
    numberOfEmployees: '',

    // Financial Details
    loanDebt: '',
    creditDebt: '',
    inventoryValue: '',
    ffeValue: '',
    ppeValue: '',
    cashValue: '',
    accountsReceivable: '',
    accountsPayable: '',

    // Owner Information
    ownerName: '',
    ownerRole: '',
    exitTimeline: '',
    sellingReason: '',

    // Transaction Goals
    transactionType: '',
    postInvolvement: '',
    priorities: [],

    // Contact Information
    email: '',
    phone: '',
    preferredContact: '',
    bestTimeToReach: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    const value = e.target.value;

    setFormData(prev => {
      if (checked) {
        return { ...prev, [name]: [...(prev[name as keyof FormData] as string[]), value] };
      } else {
        return {
          ...prev,
          [name]: (prev[name as keyof FormData] as string[]).filter(item => item !== value)
        };
      }
    });
  };

  const validateStep = (step: number) => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      // Business Information validation
      if (!formData.businessName) newErrors.businessName = 'Business name is required';
      if (!formData.industry) newErrors.industry = 'Industry is required';
      if (!formData.annualRevenue) newErrors.annualRevenue = 'Annual revenue is required';
      if (!formData.annualEbitda) newErrors.annualEbitda = 'Business model is required';
    }
    else if (step === 2) {
      // Financial Details validation
      if (!formData.loanDebt) newErrors.loanDebt = 'Primary product information is required';
    }
    else if (step === 3) {
      // Owner Information validation
      if (!formData.ownerName) newErrors.ownerName = 'Owner name is required';
      if (!formData.exitTimeline) newErrors.exitTimeline = 'Exit timeline is required';
    }
    else if (step === 4) {
      // No required fields for Transaction Goals
    }
    else if (step === 5) {
      // Contact Information validation
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData.phone) newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!validateStep(currentStep)) {
    return;
  }

  setIsSubmitting(true);
  try {
    const params = new URLSearchParams();
    params.append('form-name', 'acquisition-inquiry');
    params.append('bot-field', '');
    params.append('subject', 'New acquisition inquiry from ACSE Capital website');

    Object.entries(formData).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => params.append(`${key}[]`, item));
      } else {
        params.append(key, String(value));
      }
    });

    const response = await fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error(`Form submission failed: ${response.status}`);
    }

    navigate('/thank-you');
  } catch (error) {
    console.error('Error submitting form:', error);
    alert('There was an error submitting your form. Please try again or contact us directly.');
  } finally {
    setIsSubmitting(false);
  }
};

  useEffect(() => {
    // Check if the URL has a success parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('submission') === 'success') {
      setFormSubmitted(true);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const renderFormStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Business Information</h2>
            <p className="text-gray-600 mb-6">Tell us about the digital business you want to discuss.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="businessName">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.businessName ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.businessName && <p className="text-red-500 text-sm mt-1">{errors.businessName}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="industry">
                  Business Type / Product Category <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.industry ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.industry && <p className="text-red-500 text-sm mt-1">{errors.industry}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="yearsInOperation">
                  Years in Operation
                </label>
                <input
                  type="text"
                  id="yearsInOperation"
                  name="yearsInOperation"
                  value={formData.yearsInOperation}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="numberOfEmployees">
                  Number of Employees
                </label>
                <input
                  type="text"
                  id="numberOfEmployees"
                  name="numberOfEmployees"
                  value={formData.numberOfEmployees}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="annualRevenue">
                  Revenue / Traction Stage <span className="text-red-500">*</span>
                </label>
                <select
                  id="annualRevenue"
                  name="annualRevenue"
                  value={formData.annualRevenue}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.annualRevenue ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select Stage</option>
                  <option value="Pre-revenue / early validation">Pre-revenue / early validation</option>
                  <option value="Under $100k annual revenue">Under $100k annual revenue</option>
                  <option value="$100k-$1M annual revenue">$100k-$1M annual revenue</option>
                  <option value="$1M-$5M annual revenue">$1M-$5M annual revenue</option>
                  <option value="$5M+ annual revenue">$5M+ annual revenue</option>
                </select>
                {errors.annualRevenue && <p className="text-red-500 text-sm mt-1">{errors.annualRevenue}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="annualEbitda">
                  Business Model <span className="text-red-500">*</span>
                </label>
                <select
                  id="annualEbitda"
                  name="annualEbitda"
                  value={formData.annualEbitda}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.annualEbitda ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select Business Model</option>
                  <option value="SaaS subscription">SaaS subscription</option>
                  <option value="Usage-based software">Usage-based software</option>
                  <option value="E-commerce">E-commerce</option>
                  <option value="Digital services">Digital services</option>
                  <option value="Media / content / audience">Media / content / audience</option>
                  <option value="Mixed">Mixed</option>
                </select>
                {errors.annualEbitda && <p className="text-red-500 text-sm mt-1">{errors.annualEbitda}</p>}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Business Profile</h2>
            <p className="text-gray-600 mb-6">Help us understand the product, audience, and operating context.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="loanDebt">
                  Primary Product or Offer <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="loanDebt"
                  name="loanDebt"
                  value={formData.loanDebt}
                  onChange={handleChange}
                  placeholder="e.g., AI workflow automation for agencies"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.loanDebt ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.loanDebt && <p className="text-red-500 text-sm mt-1">{errors.loanDebt}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="creditDebt">
                  Primary Customer Type
                </label>
                <input
                  type="text"
                  id="creditDebt"
                  name="creditDebt"
                  value={formData.creditDebt}
                  onChange={handleChange}
                  placeholder="e.g., SMB teams, creators, e-commerce brands"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="inventoryValue">
                  Main Distribution Channel
                </label>
                <input
                  type="text"
                  id="inventoryValue"
                  name="inventoryValue"
                  value={formData.inventoryValue}
                  onChange={handleChange}
                  placeholder="e.g., SEO, paid acquisition, outbound, partnerships"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="ffeValue">
                  Core Technology Stack
                </label>
                <input
                  type="text"
                  id="ffeValue"
                  name="ffeValue"
                  value={formData.ffeValue}
                  onChange={handleChange}
                  placeholder="e.g., OpenAI, Next.js, Stripe, Supabase"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="ppeValue">
                  Current Traction Signals
                </label>
                <input
                  type="text"
                  id="ppeValue"
                  name="ppeValue"
                  value={formData.ppeValue}
                  onChange={handleChange}
                  placeholder="e.g., ARR, active users, retention, growth rate"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="cashValue">
                  What Makes the Business Defensible?
                </label>
                <input
                  type="text"
                  id="cashValue"
                  name="cashValue"
                  value={formData.cashValue}
                  onChange={handleChange}
                  placeholder="e.g., niche positioning, distribution edge, proprietary workflow"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="accountsReceivable">
                  Team or Operating Model
                </label>
                <input
                  type="text"
                  id="accountsReceivable"
                  name="accountsReceivable"
                  value={formData.accountsReceivable}
                  onChange={handleChange}
                  placeholder="e.g., solo founder, lean team, agency-supported"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="accountsPayable">
                  Biggest Current Bottleneck
                </label>
                <input
                  type="text"
                  id="accountsPayable"
                  name="accountsPayable"
                  value={formData.accountsPayable}
                  onChange={handleChange}
                  placeholder="e.g., growth, retention, product delivery, hiring"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Founder / Operator Information</h2>
            <p className="text-gray-600 mb-6">Tell us about the person leading the business and the transition context.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="ownerName">
                  Owner's Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="ownerName"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.ownerName ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.ownerName && <p className="text-red-500 text-sm mt-1">{errors.ownerName}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="ownerRole">
                  Role in the Business
                </label>
                <input
                  type="text"
                  id="ownerRole"
                  name="ownerRole"
                  value={formData.ownerRole}
                  onChange={handleChange}
                  placeholder="e.g., CEO, Managing Director"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="exitTimeline">
                  Timeline for a Possible Transition <span className="text-red-500">*</span>
                </label>
                <select
                  id="exitTimeline"
                  name="exitTimeline"
                  value={formData.exitTimeline}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.exitTimeline ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select Timeline</option>
                  <option value="Immediate (0-6 months)">Immediate (0-6 months)</option>
                  <option value="Short-term (6-12 months)">Short-term (6-12 months)</option>
                  <option value="Medium-term (1-2 years)">Medium-term (1-2 years)</option>
                  <option value="Long-term (3-5 years)">Long-term (3-5 years)</option>
                  <option value="Exploratory only">Exploratory only</option>
                </select>
                {errors.exitTimeline && <p className="text-red-500 text-sm mt-1">{errors.exitTimeline}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 font-medium mb-2" htmlFor="sellingReason">
                  Reason for Reaching Out
                </label>
                <textarea
                  id="sellingReason"
                  name="sellingReason"
                  value={formData.sellingReason}
                  onChange={handleChange}
                  rows={4}
                  placeholder="e.g., acquisition interest, strategic partnership, founder transition, venture discussion"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                ></textarea>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Opportunity Goals</h2>
            <p className="text-gray-600 mb-6">Help us understand what kind of conversation you want to have.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="transactionType">
                  Type of Opportunity
                </label>
                <select
                  id="transactionType"
                  name="transactionType"
                  value={formData.transactionType}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Opportunity Type</option>
                  <option value="Full acquisition">Full acquisition</option>
                  <option value="Majority acquisition">Majority acquisition</option>
                  <option value="Strategic partnership">Strategic partnership</option>
                  <option value="Venture / startup conversation">Venture / startup conversation</option>
                  <option value="Not sure yet">Not sure yet</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="postInvolvement">
                  Preferred Founder Involvement
                </label>
                <select
                  id="postInvolvement"
                  name="postInvolvement"
                  value={formData.postInvolvement}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Involvement Level</option>
                  <option value="Complete exit">Complete exit</option>
                  <option value="Stay on as advisor">Stay on as advisor</option>
                  <option value="Continue in operational role">Continue in operational role</option>
                  <option value="Gradual transition out">Gradual transition out</option>
                  <option value="Not sure yet">Not sure yet</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-gray-700 font-medium mb-2">
                  Key Priorities
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="priority-value"
                      name="priorities"
                      value="Right strategic fit"
                      checked={formData.priorities.includes('Right strategic fit')}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="priority-value" className="ml-2 text-gray-700">
                      Right strategic fit
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="priority-legacy"
                      name="priorities"
                      value="Preserving product quality"
                      checked={formData.priorities.includes('Preserving product quality')}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="priority-legacy" className="ml-2 text-gray-700">
                      Preserving product quality
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="priority-employees"
                      name="priorities"
                      value="Team continuity"
                      checked={formData.priorities.includes('Team continuity')}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="priority-employees" className="ml-2 text-gray-700">
                      Team continuity
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="priority-growth"
                      name="priorities"
                      value="Growth support after transition"
                      checked={formData.priorities.includes('Growth support after transition')}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="priority-growth" className="ml-2 text-gray-700">
                      Growth support after transition
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="priority-speed"
                      name="priorities"
                      value="Speed and clarity"
                      checked={formData.priorities.includes('Speed and clarity')}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="priority-speed" className="ml-2 text-gray-700">
                      Speed and clarity
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Contact Information</h2>
            <p className="text-gray-600 mb-6">How can we reach you to discuss the business further?</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="email">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="phone">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="preferredContact">
                  Preferred Method of Contact
                </label>
                <select
                  id="preferredContact"
                  name="preferredContact"
                  value={formData.preferredContact}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Preference</option>
                  <option value="Email">Email</option>
                  <option value="Phone">Phone</option>
                  <option value="Either">Either</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2" htmlFor="bestTimeToReach">
                  Best Time to Reach You
                </label>
                <input
                  type="text"
                  id="bestTimeToReach"
                  name="bestTimeToReach"
                  value={formData.bestTimeToReach}
                  onChange={handleChange}
                  placeholder="e.g., Weekday mornings, Afternoons"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="mt-6 bg-blue-50 p-4 rounded-lg flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <Info size={20} className="text-primary-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-primary-800">Privacy Notice</h3>
                <p className="text-sm text-primary-700 mt-1">
                  Your information is kept strictly confidential. We will only use it to evaluate potential acquisition or venture opportunities and will not share it with third parties without your consent.
                </p>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Review & Submit</h2>
            <p className="text-gray-600 mb-6">Please review your information before submitting.</p>

            <div className="bg-gray-50 p-6 rounded-lg space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Business Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Business Name</p>
                    <p className="text-gray-800">{formData.businessName || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Industry/Sector</p>
                    <p className="text-gray-800">{formData.industry || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Revenue / Traction Stage</p>
                    <p className="text-gray-800">{formData.annualRevenue || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Business Model</p>
                    <p className="text-gray-800">{formData.annualEbitda || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Business Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Primary Product or Offer</p>
                    <p className="text-gray-800">{formData.loanDebt || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Main Distribution Channel</p>
                    <p className="text-gray-800">{formData.inventoryValue || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Founder / Operator Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Primary Contact Name</p>
                    <p className="text-gray-800">{formData.ownerName || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Transition Timeline</p>
                    <p className="text-gray-800">{formData.exitTimeline || 'Not provided'}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-800">{formData.email || 'Not provided'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-gray-800">{formData.phone || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-yellow-50 p-4 rounded-lg flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <AlertCircle size={20} className="text-yellow-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Before You Submit</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  By submitting this form, you confirm that the information provided is accurate to the best of your knowledge and that you want to discuss a potential acquisition or venture opportunity with ACSE Capital.
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div id="seller-form" className="max-w-4xl mx-auto pt-10">
      {formSubmitted ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center py-16"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
            <Check size={32} className="text-green-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Thank You!</h2>
          <p className="text-xl text-gray-600 mb-8">
            Your information has been submitted successfully. We'll review your details and get back to you soon.
          </p>
          <button
            onClick={onBack}
            className="px-6 py-3 rounded-lg bg-primary-600 text-white font-medium transition-all duration-300 hover:bg-primary-500"
          >
            Return to Home
          </button>
        </motion.div>
      ) : (
        <div>
          {/* Progress bar */}
          <div className="mb-10">
            <div className="flex justify-between items-center mb-2">
              {[1, 2, 3, 4, 5, 6].map((step) => (
                <div
                  key={step}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step}
                </div>
              ))}
            </div>
            <div className="h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-primary-600 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep - 1) / 5) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>Business Info</span>
              <span>Profile</span>
              <span>Owner</span>
              <span>Goals</span>
              <span>Contact</span>
              <span>Review</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {renderFormStep()}

            <div className="mt-10 flex justify-between">
              {currentStep > 1 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={prevStep}
                  className="flex items-center px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium transition-all duration-300 hover:bg-gray-50"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Back
                </motion.button>
              )}

              {currentStep === 1 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={onBack}
                  className="flex items-center px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium transition-all duration-300 hover:bg-gray-50"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Back to Info
                </motion.button>
              )}

              {currentStep < 6 ? (
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)" }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={nextStep}
                  className="flex items-center px-6 py-3 rounded-lg bg-primary-600 text-white font-medium transition-all duration-300 hover:bg-primary-500 ml-auto"
                >
                  Next
                  <ArrowRight size={18} className="ml-2" />
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 15px -3px rgba(59, 130, 246, 0.3)" }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center px-6 py-3 rounded-lg bg-primary-600 text-white font-medium transition-all duration-300 hover:bg-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed ml-auto"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                  <Check size={18} className="ml-2" />
                </motion.button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SellerForm;

